import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../firebase/auth.tsx";
import "../App.css";
import "../styles/main.css";
import styles from "../styles/headquarterList.module.css";
import ConfirmDeleteHQModal from "./modals/confirmDeleteHQModal.tsx";
import ChangeHQDataModal from "./modals/changeHQDataModal.tsx";
import ConfirmHideModal from "./modals/confirmHideModal.tsx";
import ConfirmPermissionModal from "./modals/confirmPermissionModal.tsx";
import {
  getHeadquarters as fetchHeadquarters,
  getTrainingsListWithPermissions,
  getComingSoonManufacturers,
  updateTrainingPermission
} from "./helpers/requests.tsx";
import { useTableFilters } from "./hooks/useTableFilters.tsx";
import TableHeaderFilter from "./common/TableHeaderFilter.tsx";

import { useTranslation } from "react-i18next";

interface SelectedTraining {
  id?: string;
  name?: string;
  entityId?: string;
  permission?: boolean;
  manufacturerId?: string;
  isHidden?: boolean;
}

const HeadquarterList = ({ headquarters, setHeadquarters }) => {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const { userId } = useAuth();
  const [isLoadingHQ, setIsLoadingHQ] = useState(false);
  const [isLoadingTrainings, setIsLoadingTrainings] = useState(false);
  const [trainingsList, setTrainingsList] = useState<any[]>([]);
  const [comingSoonManufacturers, setComingSoonManufacturers] = useState<any[]>(
    []
  );
  const [selectedHQId, setSelectedHQId] = useState("");
  const [selectedHQName, setSelectedHQName] = useState("");
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [editingHQ, setEditingHQ] = useState<{
    [key: string]: boolean;
  }>({});
  const [showChangeDataModal, setShowChangeDataModal] = useState(false);

  const [visibility, setVisibility] = useState({});
  const [showConfirmHideModal, setShowConfirmHideModal] = useState(false);
  const [showConfirmPermissionModal, setShowConfirmPermissionModal] =
    useState<SelectedTraining | false>(false);
  const [selectedManufacturerId, setSelectedManufacturerId] = useState("");
  const [selectedManufacturerName, setSelectedManufacturerName] = useState("");
  const [selectedTraining, setSelectedTraining] = useState<SelectedTraining>(
    {}
  );
  const [collapsedManufacturers, setCollapsedManufacturers] = useState<{
    [key: string]: boolean;
  }>({});
  const [pendingSelectAll, setPendingSelectAll] = useState<boolean>(true);
  const [pendingAction, setPendingAction] = useState<'selectAll' | 'hide'>('selectAll');

  const navigate = useNavigate();

  const { t, i18n } = useTranslation();

  // Use the new hook for filtering
  const {
    filteredData: filteredHeadquarters,
    filterPanels,
    filters,
    searchQueries,
    panelRefs,
    toggleFilterPanel,
    handleFilterChange,
    handleFilterSearch,
    setFilters
  } = useTableFilters(headquarters);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingHQ(true);
      try {
        const data = await fetchHeadquarters();
        setHeadquarters(data);
      } catch (error) {
        console.error("Error getting headquarters:", error);
      }
      setIsLoadingHQ(false);
    };

    fetchData();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOptionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);

      setHeadquarters([]);
      setSelectedHQId("");
      setSelectedHQName("");
      setEditingHQ({});
      setTrainingsList([]);
    };
  }, [setHeadquarters]);

  const fetchHQTrainingsList = async (headquarterId: string) => {
    setIsLoadingTrainings(true);
    try {
      const trainings = await getTrainingsListWithPermissions(headquarterId, "headquarter", true);
      setTrainingsList(trainings);

      // Initialize visibility state
      const initialVisibility = {};
      trainings.forEach((training) => {
        initialVisibility[training.manufacturerId] =
          initialVisibility[training.manufacturerId] || training.isHidden;
      });
      setVisibility(initialVisibility);
    } catch (error) {
      console.error("Error get headquarter trainings list:", error);
    } finally {
      setIsLoadingTrainings(false);
    }
  };

  const fetchComingSoonManufacturers = async (headquarterId: string) => {
    setIsLoadingTrainings(true);

    try {
      const manufacturers = await getComingSoonManufacturers(headquarterId, "headquarter", true);
      setComingSoonManufacturers(manufacturers);

      setVisibility((prevVisibility) => {
        const updatedVisibility = { ...prevVisibility };
        manufacturers.forEach((manufacturer) => {
          updatedVisibility[manufacturer.id] = manufacturer.isHidden;
        });
        return updatedVisibility;
      });
    } catch (error) {
      console.error("Error get coming soon manufacturers:", error);
    } finally {
      setIsLoadingTrainings(false);
    }
  };

  const handleUpdateHQPermission = async (
    headquarterId: string,
    trainingId: string,
    isChecked: boolean,
    isHidden: boolean = false
  ) => {
    try {
      await updateTrainingPermission(headquarterId, "headquarter", trainingId, isChecked, isHidden);
    } catch (error) {
      console.error("Error update headquarter training permission:", error);
    }
  };

  const toggleDropdown = () => {
    setIsOptionsOpen(!isOptionsOpen);
  };

  const handleViewClick = async (
    headquarterId: string,
    headquarterName: string
  ) => {
    setIsLoadingHQ(true);
    navigate(`/userList/headquarter/${headquarterId}`);
  };

  const handleEditClick = async (
    headquarterId: string,
    headquarterName: string
  ) => {
    setEditingHQ((prevState) => {
      const newState = { ...prevState };
      Object.keys(newState).forEach((key) => {
        newState[key] = false;
      });
      newState[headquarterId] = true;
      return newState;
    });

    setSelectedHQId(headquarterId);
    setSelectedHQName(headquarterName);

    await fetchHQTrainingsList(headquarterId);
    await fetchComingSoonManufacturers(headquarterId);
  };

  const handleDeleteHQ = () => {
    setHeadquarters((prevHQ) =>
      prevHQ.filter((headquarter) => headquarter.id !== selectedHQId)
    );
    setSelectedHQId("");
    setSelectedHQName("");
    setEditingHQ({});
    setTrainingsList([]);
    setShowConfirmDeleteModal(false);
  };

  const handleChangeData = async (newHQName: string) => {
    setHeadquarters((prevHQ) =>
      prevHQ.map((headquarter) => {
        if (headquarter.id === selectedHQId) {
          return {
            ...headquarter,
            name: newHQName,
          };
        }
        return headquarter;
      })
    );

    setSelectedHQName(newHQName);
    setShowChangeDataModal(false);
  };

  const renderConfirmDeleteHQModal = () => {
    return (
      <ConfirmDeleteHQModal
        setShowConfirmDeleteHQModal={setShowConfirmDeleteModal}
        headquarterId={selectedHQId}
        headquarterName={selectedHQName}
        handleDeleteHQ={handleDeleteHQ}
      />
    );
  };

  const renderChangeDataModal = () => {
    return (
      <ChangeHQDataModal
        setShowChangeDataModal={setShowChangeDataModal}
        headquarterId={selectedHQId}
        headquarterName={selectedHQName}
        handleChangeData={handleChangeData}
      />
    );
  };

  const renderConfirmPermissionModal = () => {
    if (typeof showConfirmPermissionModal === 'boolean' && !showConfirmPermissionModal) return null; // If false, don't render

    const { id, name, entityId, permission } = showConfirmPermissionModal as SelectedTraining;

    return (
      <ConfirmPermissionModal
        setShowConfirmPermissionModal={() => setShowConfirmPermissionModal(false)}
        trainingId={id}
        trainingName={name}
        targetId={entityId}
        targetName={selectedHQName}
        permission={permission}
        handlePermissionChange={handlePermissionChange}
      />
    );
  };

  const handlePermissionChange = async (
    trainingId: string,
    targetId: string,
    newPermissionState: boolean
  ) => {
    const originalTrainingsList = trainingsList; // Store original for rollback
    setTrainingsList(
      trainingsList.map((training) => {
        if (training.id === trainingId) {
          return { ...training, permission: newPermissionState };
        }
        return training;
      })
    );

    try {
      await handleUpdateHQPermission(targetId, trainingId, newPermissionState);
    } catch (error) {
      console.error("Error updating headquarter training permission:", error);
      setTrainingsList(originalTrainingsList);
      alert(t("Failed to update permission. Please try again."));
    }
    setSelectedTraining({});
  };

  const toggleCollapse = (manufacturerId: string) => {
    setCollapsedManufacturers((prev) => ({
      ...prev,
      [manufacturerId]: !prev[manufacturerId],
    }));
  };

  const handleSelectAll = async (
    manufacturerId: string,
    shouldSelect: boolean
  ) => {
    setShowConfirmHideModal(false);
    const originalTrainingsList = [...trainingsList];

    // Optimistic update
    const updatedTrainings = trainingsList.map((training) => {
      if (training.manufacturerId === manufacturerId) {
        return { ...training, permission: shouldSelect };
      }
      return training;
    });
    setTrainingsList(updatedTrainings);

    // Identify changes needed
    const promises = originalTrainingsList
      .filter(
        (training) =>
          training.manufacturerId === manufacturerId &&
          training.permission !== shouldSelect
      )
      .map((training) =>
        handleUpdateHQPermission(
          training.entityId,
          training.id,
          shouldSelect,
          training.isHidden
        )
      );

    try {
      await Promise.all(promises);
    } catch (error) {
      console.error("Error in bulk permission update:", error);
      setTrainingsList(originalTrainingsList); // Rollback on error
      alert(t("Failed to update permissions. Please try again."));
    }
  };

  const handleHideClick = async (manufacturerId: string) => {
    setShowConfirmHideModal(false);
    const isHidden = visibility[manufacturerId];
    const shouldHide = !isHidden;

    // Update visibility state
    setVisibility((prev) => ({
      ...prev,
      [manufacturerId]: shouldHide,
    }));

    if (
      comingSoonManufacturers.some(
        (comingSoonManufacturer) => comingSoonManufacturer.id === manufacturerId
      )
    ) {
      // Coming soon logic
      const updatedComingSoonManufacturers = comingSoonManufacturers.map(
        (comingSoonManufacturer) => {
          if (comingSoonManufacturer.id === manufacturerId) {
            handleUpdateHQPermission(
              selectedHQId,
              comingSoonManufacturer.id,
              true,
              shouldHide
            );
            return { ...comingSoonManufacturer, isHidden: shouldHide };
          }
          return comingSoonManufacturer;
        }
      );
      setComingSoonManufacturers(updatedComingSoonManufacturers);
    } else {
      // Regular trainings logic
      const updatedTrainings = trainingsList.map((training) => {
        if (training.manufacturerId === manufacturerId) {
          handleUpdateHQPermission(selectedHQId, training.id, false, shouldHide);
          return { ...training, permission: false, isHidden: shouldHide };
        }
        return training;
      });
      setTrainingsList(updatedTrainings);
    }
  };

  const handleToggleHide = (manufacturerId, manufacturerName) => {
    const isHidden = visibility[manufacturerId];
    // Always show confirmation modal
    setSelectedManufacturerId(manufacturerId);
    setSelectedManufacturerName(manufacturerName);
    setPendingAction('hide');
    setShowConfirmHideModal(true);
  };

  const renderConfirmHideModal = () => {
    let title = "";
    let description = "";
    let handleAction = (id) => { };

    if (pendingAction === 'selectAll') {
      title = pendingSelectAll ? t("Select_all") : t("Unselect_all");
      description = pendingSelectAll
        ? t("Confirm_to_add_the_permission") + " " + t("for_all_users_in_the_headquarter")
        : t("Confirm_to_remove_the_permission") + " " + t("for_all_users_in_the_headquarter");
      handleAction = (manufacturerId) => handleSelectAll(manufacturerId, pendingSelectAll);
    } else {
      const isHidden = visibility[selectedManufacturerId];
      title = isHidden ? t("Show_Manufacturer") : t("Hide_Manufacturer");
      description = isHidden
        ? t("Confirm_to_show_this_manufacturer") + " " + t("for_all_users_in_the_headquarter")
        : t("Confirm_to_hide_this_manufacturer") + " " + t("for_all_users_in_the_headquarter");
      handleAction = handleHideClick;
    }

    return (
      <ConfirmHideModal
        setShowConfirmHideModal={setShowConfirmHideModal}
        manufacturerId={selectedManufacturerId}
        manufacturerName={selectedManufacturerName}
        headquarterView={true}
        handleHideClick={handleAction}
        title={title}
        description={description}
        confirmText={t("Confirm")}
        cancelText={t("Cancel")}
      />
    );
  };

  const groupTrainingsByManufacturer = (trainings) => {
    const groupedTrainings = {};
    trainings.forEach((training) => {
      const {
        id,
        name,
        manufacturerId,
        manufacturerName,
        permission,
        entityId,
        isHidden,
      } = training;
      if (!groupedTrainings[manufacturerId]) {
        groupedTrainings[manufacturerId] = {
          manufacturerName: manufacturerName,
          trainings: [],
        };
      }
      groupedTrainings[manufacturerId].trainings.push({
        id: id,
        name: name,
        permission: permission,
        entityId: entityId,
        isHidden: isHidden,
        manufacturerName: manufacturerName,
      });
    });

    return groupedTrainings;
  };

  const renderGroupedTrainings = (groupedTrainings: {
    [manufacturerId: string]: {
      manufacturerName: string;
      trainings: {
        id: string;
        entityId: string;
        name: string;
        permission: boolean;
        isHidden: boolean;
      }[];
    };
  }) => {
    return (
      <>
        {Object.entries(groupedTrainings).map(
          ([manufacturerId, { manufacturerName, trainings }]) => {
            const isCollapsed = collapsedManufacturers[manufacturerId];
            const allSelected = trainings.every((t) => t.permission);
            const isManufacturerHidden = trainings.every(
              (training) => training.isHidden
            );

            return (
              <div key={manufacturerId} className={styles.trainingsGroup}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {!isManufacturerHidden && ( // Only show collapse/expand if not hidden
                    <span
                      onClick={() => toggleCollapse(manufacturerId)}
                      style={{
                        cursor: "pointer",
                        marginRight: "10px",
                        fontSize: "0.8em",
                        userSelect: "none",
                      }}
                    >
                      {isCollapsed ? "\u25B6" : "\u25BC"}
                    </span>
                  )}
                  <label
                    className={styles.trainingsGroupTitle}
                    style={{
                      color: isManufacturerHidden ? "gray" : "#39a686",
                      cursor: "pointer",
                      marginBottom: 0
                    }}
                    onClick={() => !isManufacturerHidden && toggleCollapse(manufacturerId)} // Only collapse if not hidden
                  >
                    {manufacturerName}
                  </label>
                  {!isManufacturerHidden && !isCollapsed && (
                    <>
                      <span
                        onClick={() => {
                          setSelectedManufacturerId(manufacturerId);
                          setSelectedManufacturerName(manufacturerName);
                          setPendingSelectAll(!allSelected);
                          setPendingAction('selectAll');
                          setShowConfirmHideModal(true);
                        }}
                        style={{
                          marginLeft: "1vh",
                          marginTop: "0.4vh",
                          fontSize: "0.7em",
                          cursor: "pointer",
                          color: "#212529"
                        }}
                      >
                        {allSelected ? t("Unselect_all") : t("Select_all")}
                      </span>
                      <span
                        onClick={() => handleToggleHide(manufacturerId, manufacturerName)}
                        style={{
                          marginLeft: "1vh",
                          marginTop: "0.4vh",
                          fontSize: "0.7em",
                          cursor: "pointer",
                          color: "#212529"
                        }}
                      >
                        {isManufacturerHidden ? t("show") : t("hide")}
                      </span>
                    </>
                  )}
                  {isManufacturerHidden && ( // Show "Show" button if hidden
                    <span
                      onClick={() => handleToggleHide(manufacturerId, manufacturerName)}
                      style={{
                        marginLeft: "1vh",
                        marginTop: "0.4vh",
                        fontSize: "0.7em",
                        cursor: "pointer",
                        color: "#212529"
                      }}
                    >
                      {t("show")}
                    </span>
                  )}
                </div>

                <div className={`${styles.collapsibleWrapper} ${isCollapsed || isManufacturerHidden ? styles.collapsed : ''}`}>
                  <div className={styles.collapsibleInner}>
                    <ul>
                      {trainings.map((training) => (
                        <li
                          key={training.id}
                          className="form-check custom-checkbox"
                          style={{ color: training.isHidden ? "gray" : "#212529" }}
                        >
                          <label className="custom-checkbox-label">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="agreeCheckbox"
                              checked={training.permission}
                              disabled={training.isHidden}
                              onChange={(e) => {
                                setShowConfirmPermissionModal({
                                  id: training.id,
                                  name: training.name,
                                  entityId: training.entityId,
                                  permission: training.permission,
                                });
                              }}
                            />
                            <span className="checkmark"></span>
                            <span className="form-check-label">
                              {training.name}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          }
        )}        {comingSoonManufacturers.length > 0 && (
          <label
            className={styles.trainingsGroupTitle}
            style={{ color: "#39a686", marginTop: "20px", display: "block" }}
          >
            {t("Coming_soon")}:
          </label>
        )}
        {comingSoonManufacturers.map(({ id, name, isHidden }) => {
          return (
            <div key={id} className={styles.trainingsGroup}>
              <label
                className={styles.trainingsGroupTitle}
                style={{ color: isHidden ? "gray" : "#212529" }} // No longer grayed out
              >
                {name}
              </label>
              <span
                onClick={() => handleToggleHide(id, name)}
                style={{
                  marginLeft: "1vh",
                  fontSize: "0.7em",
                  cursor: "pointer",
                  color: "#212529"
                }}
              >
                {isHidden ? t("show") : t("hide")}
              </span>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className={styles.adminContainer}>
      {isLoadingHQ ? (
        <p className={styles.loading}>{t("Loading...")}</p>
      ) : (
        <>
          {/* Left side */}
          <div className={styles.leftContainer}>
            {/* Separate table header - don't find other way to avoid overlaping header on scroll */}
            <div className={styles.tableHeader}>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">
                      <TableHeaderFilter
                        column="name"
                        title={t("Headquarter")}
                        data={headquarters} // We filter against all data
                        filterPanels={filterPanels}
                        filters={filters}
                        searchQueries={searchQueries}
                        panelRefs={panelRefs}
                        toggleFilterPanel={toggleFilterPanel}
                        handleFilterChange={handleFilterChange}
                        handleFilterSearch={handleFilterSearch}
                        setFilters={setFilters}
                      />
                    </th>
                    <th scope="col" className={styles.editButtonWrapper}></th>
                    <th scope="col" className={styles.editButtonWrapper}></th>
                  </tr>
                </thead>
              </table>
            </div>

            {/* Table with restricted height and scrollbar */}
            <div className={styles.tableContent}>
              <div className={`container-fluid ${styles.containerFluid}`}>
                <table className="table">
                  <tbody>
                    {[...filteredHeadquarters].sort((a, b) => a.name.localeCompare(b.name)).map((headquarter: any, index: number) => (
                      <tr
                        key={headquarter.id}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#D4E5E2" : "transparent",
                        }}
                      >
                        <td>
                          {" "}
                          <img
                            src="/images/Icon_HQ.png"
                            style={{
                              width: "2.5vh",
                              minWidth: "10px",
                              marginLeft: "0.3vh",
                              marginRight: "1vh",
                            }}
                          />
                          {headquarter.name}
                        </td>
                        <td className={styles.editButtonWrapper}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() =>
                              handleViewClick(headquarter.id, headquarter.name)
                            }
                            style={{
                              backgroundColor: "#39A686",
                              borderColor: "#39A686",
                            }}
                          >
                            {t("View")}
                          </button>
                        </td>
                        <td className={styles.editButtonWrapper}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() =>
                              handleEditClick(headquarter.id, headquarter.name)
                            }
                            style={{
                              backgroundColor: editingHQ[headquarter.id]
                                ? "#404040"
                                : "#39A686",
                              borderColor: editingHQ[headquarter.id]
                                ? "#404040"
                                : "#39A686",
                            }}
                          >
                            {t("Details")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* Right side */}
          <div className={styles.rightContainer}>
            {selectedHQId ? (
              <>
                <div className={styles.containerHeaderWrapper}>
                  <div className={styles.containerHeader}>
                    <div className={styles.userDataWrapper}>
                      <div className={styles.userDataName}>
                        <img
                          src="/images/Icon_HQ.png"
                          style={{
                            width: "5vh",
                            minWidth: "25px",
                            marginRight: "15px",
                          }}
                        />
                        {selectedHQName}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div className="dropdown" ref={dropdownRef}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={toggleDropdown}
                        style={{
                          width: "13vh",
                          minWidth: "60px",
                          backgroundColor: "#39A686",
                          borderColor: "#39A686",
                          borderRadius: "10px",
                          fontSize: "1em",
                          fontFamily: "VirtualLabQuickSandLight",
                          fontWeight: "bold",
                          paddingRight: "30px",
                        }}
                      >
                        <span
                          style={{
                            marginRight: "1.8vh",
                          }}
                        >
                          &#9013;
                        </span>
                        {t("Options")}
                      </button>
                      {isOptionsOpen && (
                        <div className={styles.dropdownMenu}>
                          <button
                            type="button"
                            onClick={() => {
                              setIsOptionsOpen(false);
                              setShowChangeDataModal(true);
                            }}
                            className="btn"
                          >
                            <img src="/images/Icon_Edit.png" />
                            {t("Edit_Headquarter")}
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setIsOptionsOpen(false);
                              setShowConfirmDeleteModal(true);
                            }}
                            className="btn"
                          >
                            <img src="/images/Icon_Delete.png" />
                            {t("Delete_Headquarter")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <hr />

                  <p className={styles.containerHeaderTitle}>
                    {t("Training_permission")}
                  </p>
                  <p className={styles.containerHeaderDescription}>
                    {t("Set_Headquarter_access_to_these_trainings_in_the_VR_application")}
                  </p>
                </div>

                <div className={`${styles.trainingsContainer} thin-scrollbar`}>
                  {isLoadingTrainings ? (
                    <p>{t("Loading...")}</p>
                  ) : (
                    renderGroupedTrainings(
                      groupTrainingsByManufacturer(trainingsList)
                    )
                  )}
                </div>
              </>
            ) : (
              <p className={styles.selectUser}>{t("Select_Headquarter_to_edit")}</p>
            )}
          </div>
        </>
      )}
      <div className={styles.adminContainer}>
        {(showConfirmDeleteModal && renderConfirmDeleteHQModal()) ||
          (showChangeDataModal && renderChangeDataModal()) ||
          (showConfirmHideModal && renderConfirmHideModal()) ||
          (showConfirmPermissionModal && renderConfirmPermissionModal())}
      </div>
    </div>
  );
};
export default HeadquarterList;